import axios from 'axios';
import { API_URL, GUEST_TOKEN } from '../config';

// 创建 axios 实例
const authApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 添加请求拦截器，自动添加 token
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || GUEST_TOKEN;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 认证服务
const authService = {
  // 登录
  login: async (identifier, password) => {
    try {
      // 登录请求不应该携带任何token，直接使用axios而不是authApi
      const response = await axios.post(`${API_URL}/api/auth/local`, {
        identifier, // 用户名或邮箱
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.jwt) {
        // 保存 token 和用户信息到本地存储
        localStorage.setItem('token', response.data.jwt);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // 尝试立即获取完整用户信息（包括头像和author信息）
      try {
        const userResponse = await authApi.get('/api/users/me?populate[avatar]=true&populate[author]=true');

        // 更新本地存储的用户信息
        if (userResponse.data) {
          // 如果author字段没有返回，尝试手动获取author信息
          if (!userResponse.data.author && userResponse.data.id) {
            try {
              // 先尝试通过user ID查找关联的author，如果失败则通过名称匹配
              let authorsResponse;
              try {
                authorsResponse = await authApi.get('/api/authors', {
                  params: {
                    'filters[user]': userResponse.data.id,
                    'populate[user]': true
                  }
                });
              } catch (filterError) {
                authorsResponse = await authApi.get('/api/authors');
              }

              if (authorsResponse.data.data && authorsResponse.data.data.length > 0) {
                // 智能匹配：如果有多条记录，找到最匹配的author
                let authorData = null;
                const userName = userResponse.data.username.toLowerCase().trim().replace(/\s+/g, '');

                // 精确匹配
                authorData = authorsResponse.data.data.find(author =>
                  author.name.toLowerCase().trim().replace(/\s+/g, '') === userName
                );

                // 如果精确匹配失败，尝试包含匹配
                if (!authorData) {
                  authorData = authorsResponse.data.data.find(author =>
                    author.name.toLowerCase().includes(userName) ||
                    userName.includes(author.name.toLowerCase())
                  );
                }

                // 如果还是没找到，使用第一条记录
                if (!authorData && authorsResponse.data.data.length > 0) {
                  authorData = authorsResponse.data.data[0];
                }

                if (authorData) {
                  // 将author信息添加到用户数据中
                  userResponse.data.author = authorData;
                  localStorage.setItem('user', JSON.stringify(userResponse.data));
                } else {
                  localStorage.setItem('user', JSON.stringify(userResponse.data));
                }
              } else {
                localStorage.setItem('user', JSON.stringify(userResponse.data));
              }
            } catch (authorError) {
              console.error('获取author信息失败:', authorError);
              localStorage.setItem('user', JSON.stringify(userResponse.data));
            }
          } else {
            localStorage.setItem('user', JSON.stringify(userResponse.data));
          }
        }
      } catch (userError) {
        console.error('获取完整用户信息失败:', userError);
        console.error('错误详情:', userError.response?.data);
        console.error('错误状态码:', userError.response?.status);

        // 如果获取author信息失败，至少保证基本用户信息
        try {
          const basicUserResponse = await authApi.get('/api/users/me?populate=avatar');
          if (basicUserResponse.data) {
            localStorage.setItem('user', JSON.stringify(basicUserResponse.data));
          }
        } catch (basicError) {
          console.error('获取基本用户信息也失败:', basicError);
          console.error('基本错误详情:', basicError.response?.data);
        }
      }
        
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('登录失败:', error);
      throw error;
    }
  },

  // 注册
  register: async (username, email, password) => {
    try {
      // 注册请求不应该携带任何token，直接使用axios而不是authApi
      const response = await axios.post(`${API_URL}/api/auth/local/register`, {
        username,
        email,
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.jwt) {
        // 保存 token 和用户信息到本地存储
        localStorage.setItem('token', response.data.jwt);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('注册失败:', error);
      throw error;
    }
  },

  // 登出
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // 获取当前用户
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // 检查是否已认证
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // 获取 token
  getToken: () => {
    return localStorage.getItem('token');
  },

  // 验证 token 是否有效
  validateToken: async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;

      // 添加populate参数获取用户头像和author信息
      const response = await authApi.get('/api/users/me?populate[avatar]=true&populate[author]=true');

      if (response.data) {
        // 更新本地存储的用户信息，确保包含最新的头像和author数据
        // 如果author字段没有返回，尝试手动获取author信息
        if (!response.data.author && response.data.id) {
          try {
            // 先尝试通过user ID查找关联的author，如果失败则通过名称匹配
            let authorsResponse;
            try {
              authorsResponse = await authApi.get('/api/authors', {
                params: {
                  'filters[user]': response.data.id,
                  'populate[user]': true
                }
              });
            } catch (filterError) {
              authorsResponse = await authApi.get('/api/authors');
            }

            if (authorsResponse.data.data && authorsResponse.data.data.length > 0) {
              // 智能匹配：如果有多条记录，找到最匹配的author
              let authorData = null;
              const userName = response.data.username.toLowerCase().trim().replace(/\s+/g, '');

              // 精确匹配
              authorData = authorsResponse.data.data.find(author =>
                author.name.toLowerCase().trim().replace(/\s+/g, '') === userName
              );

              // 如果精确匹配失败，尝试包含匹配
              if (!authorData) {
                authorData = authorsResponse.data.data.find(author =>
                  author.name.toLowerCase().includes(userName) ||
                  userName.includes(author.name.toLowerCase())
                );
              }

              // 如果还是没找到，使用第一条记录
              if (!authorData && authorsResponse.data.data.length > 0) {
                authorData = authorsResponse.data.data[0];
              }

              if (authorData) {
                // 将author信息添加到用户数据中
                response.data.author = authorData;
                localStorage.setItem('user', JSON.stringify(response.data));
              } else {
                localStorage.setItem('user', JSON.stringify(response.data));
              }
            } else {
              localStorage.setItem('user', JSON.stringify(response.data));
            }
          } catch (authorError) {
            console.error('Token验证获取author信息失败:', authorError);
            localStorage.setItem('user', JSON.stringify(response.data));
          }
        } else {
          localStorage.setItem('user', JSON.stringify(response.data));
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token 验证失败:', error);
      console.error('Token验证错误详情:', error.response?.data);
      console.error('Token验证错误状态码:', error.response?.status);

      // 如果 token 无效，清除本地存储
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        authService.logout();
      }
      return false;
    }
  },

  // 检查用户名是否已存在
  checkUsernameExists: async (username) => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          filters: {
            username: {
              $eq: username
            }
          }
        }
      });

      return response.data && response.data.length > 0;
    } catch (error) {
      console.error('检查用户名失败:', error);
      throw new Error('检查用户名是否存在时发生错误');
    }
  },

  // 更新用户名
  updateUsername: async (newUsername) => {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser || !currentUser.id) {
        throw new Error('未找到当前用户信息');
      }

      // 如果新用户名与当前用户名相同，直接返回
      if (currentUser.username === newUsername) {
        return currentUser;
      }

      const exists = await authService.checkUsernameExists(newUsername);
      if (exists) {
        throw new Error('用户名已被使用');
      }

      const response = await axios.put(
        `${API_URL}/api/users/${currentUser.id}`,
        { username: newUsername },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data) {
        // 更新本地存储的用户信息
        const updatedUser = { ...currentUser, ...response.data };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return updatedUser;
      }
      throw new Error('更新用户名失败');
    } catch (error) {
      console.error('更新用户名失败:', error);
      if (error.message === '用户名已被使用') {
        throw error;
      }
      throw new Error('更新用户名失败，请重试');
    }
  },

  // 更新密码
  updatePassword: async (currentPassword, newPassword) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/change-password`,
        {
          currentPassword,
          password: newPassword,
          passwordConfirmation: newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('更新密码失败:', error);
      if (error.response?.status === 401) {
        throw new Error('当前密码错误');
      }
      throw new Error('更新密码失败，请重试');
    }
  },

  // 更新用户头像
  updateUserAvatar: async (fileId) => {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser || !currentUser.id) {
        throw new Error('未找到当前用户信息');
      }

      // 更新用户头像
      await axios.put(
        `${API_URL}/api/users/${currentUser.id}`,
        { avatar: fileId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // 获取完整的用户信息（包括头像）
      const userResponse = await axios.get(`${API_URL}/api/users/me?populate=*`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!userResponse.data) {
        throw new Error('获取更新后的用户信息失败');
      }
      
      // 更新本地存储的用户信息
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      return userResponse.data;
    } catch (error) {
      console.error('更新头像失败:', error);
      throw new Error('更新头像失败，请重试');
    }
  }
};

export default authService;